// frontend/openaiModal.jsx
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  FormHelperText
} from "@material-ui/core";
import { 
  Visibility, 
  VisibilityOff, 
  ExpandMore,
  Add,
  Delete,
  Info,
  Settings,
  Timer,
  Chat,
  Business,
  Person,
  LocalOffer,
  ViewColumn,
  Event,
  PlayArrow,
  Stop,
  AttachFile,
  VolumeUp
} from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";
import api from "../../services/api";
import { useTtsPreview } from "../../hooks/useTtsPreview";

// Lista de modelos OpenAI suportados
const openAiModels = [
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106", 
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "gpt-4o-mini"
];

// Vozes Azure (PT-BR Neural)
const azureVoices = [
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural"
];

// [TTS OPCIONAL 778-L2] Vozes/modelos OpenAI TTS — usados SÓ quando o nó escolhe explicitamente o provedor OpenAI
const openAiTtsVoices = [
  "alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer",
  "ballad", "verse", "marin", "cedar"
];

const openAiVoiceProfiles = {
  alloy: { gender: "Neutra", tags: ["equilibrada", "clara", "comercial"] },
  ash: { gender: "Masculina", tags: ["grave", "séria", "consultiva"] },
  coral: { gender: "Feminina", tags: ["calma", "acolhedora", "suave"] },
  echo: { gender: "Masculina", tags: ["firme", "objetiva", "profissional"] },
  fable: { gender: "Neutra", tags: ["expressiva", "narrativa", "energética"] },
  nova: { gender: "Feminina", tags: ["moderna", "leve", "comercial"] },
  onyx: { gender: "Masculina", tags: ["profunda", "marcante", "institucional"] },
  sage: { gender: "Neutra", tags: ["calma", "didática", "natural"] },
  shimmer: { gender: "Feminina", tags: ["brilhante", "amistosa", "leve"] },
  ballad: { gender: "Neutra", tags: ["calma", "suave", "GPT-4o mini"] },
  verse: { gender: "Neutra", tags: ["fluida", "moderna", "GPT-4o mini"] },
  marin: { gender: "Feminina", tags: ["serena", "elegante", "GPT-4o mini"] },
  cedar: { gender: "Masculina", tags: ["estável", "sóbria", "GPT-4o mini"] }
};

const azureVoiceProfiles = {
  "pt-BR-FabioNeural": { gender: "Masculina", tags: ["clássica", "comercial", "objetiva"] },
  "pt-BR-FranciscaNeural": { gender: "Feminina", tags: ["acolhedora", "clara", "natural"] },
  "pt-BR-AntonioNeural": { gender: "Masculina", tags: ["institucional", "firme", "grave"] },
  "pt-BR-BrendaNeural": { gender: "Feminina", tags: ["moderna", "leve", "energética"] },
  "pt-BR-DonatoNeural": { gender: "Masculina", tags: ["segura", "séria", "consultiva"] },
  "pt-BR-ElzaNeural": { gender: "Feminina", tags: ["calma", "didática", "profissional"] },
  "pt-BR-GiovannaNeural": { gender: "Feminina", tags: ["suave", "amistosa", "comercial"] },
  "pt-BR-HumbertoNeural": { gender: "Masculina", tags: ["natural", "equilibrada", "corporativa"] },
  "pt-BR-JulioNeural": { gender: "Masculina", tags: ["dinâmica", "clara", "moderna"] },
  "pt-BR-LeilaNeural": { gender: "Feminina", tags: ["serena", "consultiva", "elegante"] },
  "pt-BR-LeticiaNeural": { gender: "Feminina", tags: ["jovial", "nítida", "leve"] },
  "pt-BR-ManuelaNeural": { gender: "Feminina", tags: ["acolhedora", "calma", "empática"] },
  "pt-BR-NicolauNeural": { gender: "Masculina", tags: ["sóbria", "grave", "segura"] },
  "pt-BR-ValerioNeural": { gender: "Masculina", tags: ["comercial", "energética", "clara"] },
  "pt-BR-YaraNeural": { gender: "Feminina", tags: ["natural", "equilibrada", "profissional"] }
};

const openAiTtsModels = [
  { value: "tts-1", label: "OpenAI TTS (rápido)" },
  { value: "tts-1-hd", label: "OpenAI TTS HD (qualidade)" },
  { value: "gpt-4o-mini-tts", label: "GPT-4o mini TTS (estilo por instrução)" }
];

// Lista legada (compatibilidade)
const availableVoices = ["texto", ...azureVoices];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  flowModeCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  temporarySettings: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.light}`
  },
  keywordChip: {
    margin: theme.spacing(0.5),
  },
  helpText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    }
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column"
  },
  voicePreviewBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50]
  },
  voicePreviewActions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  voiceCatalog: {
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: "hidden"
  },
  voiceCatalogHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(130px, 160px) minmax(100px, 120px) 1fr auto",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey[100],
    fontWeight: 700,
    fontSize: "0.8rem"
  },
  voiceCatalogRow: {
    display: "grid",
    gridTemplateColumns: "minmax(130px, 160px) minmax(100px, 120px) 1fr auto",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    }
  },
  voiceTags: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap"
  },
  voiceTagChip: {
    height: 22,
    fontSize: "0.72rem"
  }
}));

// Esquema de validação para OpenAI
const OpenAiSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(openAiModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  apiKey: Yup.string().required("Informe a API Key"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  delay: Yup.number()
    .min(0, "Mínimo 0 segundos")
    .max(60, "Máximo 60 segundos")
    .default(7),
  voice: Yup.string().required("Informe o modo para Voz"),
  ttsProvider: Yup.string().oneOf(["azure", "openai"]).default("azure"),
  ttsModel: Yup.string().when(["voice", "ttsProvider"], {
    is: (voice, ttsProvider) => voice !== "texto" && ttsProvider === "openai",
    then: Yup.string().required("Selecione o modelo OpenAI TTS"),
    otherwise: Yup.string().notRequired()
  }),
  voiceKey: Yup.string().when(["voice", "ttsProvider"], {
    is: (voice, ttsProvider) => voice !== "texto" && (ttsProvider || "azure") === "azure",
    then: Yup.string().required("Voice Key é obrigatória para síntese de voz Azure"),
    otherwise: Yup.string().notRequired()
  }),
  voiceRegion: Yup.string().when(["voice", "ttsProvider"], {
    is: (voice, ttsProvider) => voice !== "texto" && (ttsProvider || "azure") === "azure",
    then: Yup.string().required("Voice Region é obrigatória para síntese de voz Azure"),
    otherwise: Yup.string().notRequired()
  }),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
  maxInteractions: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      // 0 = ilimitado (desabilita o limite no backend)
      .min(0, "0 = ilimitado")
      .max(50, "Máximo 50 interações")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  completionTimeout: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      // 0 = sem limite (desabilita o timeout no backend)
      .min(0, "0 = sem limite")
      .max(60, "Máximo 60 minutos")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  continueKeywords: Yup.array().when("flowMode", {
    is: "temporary",
    then: Yup.array()
      .of(Yup.string().required("Palavra-chave não pode estar vazia"))
      .min(1, "Pelo menos uma palavra-chave é obrigatória no modo temporário"),
    otherwise: Yup.array()
  }),
  objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
    is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
    then: Yup.string().required("Objetivo é obrigatório quando auto completar está ativo"),
    otherwise: Yup.string()
  })
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-4o",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    ttsProvider: "azure",
    ttsModel: "tts-1",
    openaiTtsInstructions: "",
    ttsSpeed: 1,
    maxTokens: 1000,
    temperature: 0.7,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    delay: 7,
    
    // Campos para controle de fluxo
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false,
    departmentTransfers: [],
    userTransfers: [],
    tagsAllowedForAI: [],
    lanesAllowedForAI: [],
    googleCalendarIntegrationId: 0,
    queueIdAfterCalendarCreate: 0,
    queueIdAfterCalendarUpdate: 0,
    queueIdAfterCalendarCancel: 0,
    sendLinksAsTextWhenVoice: false,
    assetsAllowedForAI: [],
    maxAttachmentsPerReply: 2
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [googleCalendarList, setGoogleCalendarList] = useState([]);
  const [queues, setQueues] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [lanesList, setLanesList] = useState([]);
  const [assetsPicker, setAssetsPicker] = useState([]);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar OpenAI ao fluxo",
    btn: "Adicionar",
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [previewText, setPreviewText] = useState("Olá, aqui é um teste de voz do atendimento. Como posso te ajudar hoje?");
  const [previewLoadingVoice, setPreviewLoadingVoice] = useState("");
  const { playPreview, stopPreview, releasePreview } = useTtsPreview();

  useEffect(() => {
    if (open === "create" || open === "edit") {
      api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => setQueues([]));
      api.get("/users", { params: { showAll: true } }).then(({ data }) => setUsersList(Array.isArray(data?.users) ? data.users : [])).catch(() => setUsersList([]));
      api.get("/tags/list").then(({ data }) => setTagsList(Array.isArray(data) ? data : [])).catch(() => setTagsList([]));
      api.get("/tag/kanban").then(({ data }) => setLanesList(data?.lista || [])).catch(() => setLanesList([]));
      api.get("/google-calendar-integrations").then(({ data }) => setGoogleCalendarList(Array.isArray(data) ? data : [])).catch(() => setGoogleCalendarList([]));
    }
  }, [open]);

  useEffect(() => {
    if (open !== "create" && open !== "edit") return;
    const params = {};
    if (integration.queueId) params.queueId = integration.queueId;
    api.get("/ai-assets/picker", { params })
      .then(({ data }) => setAssetsPicker(Array.isArray(data) ? data : []))
      .catch(() => setAssetsPicker([]));
  }, [open, integration.queueId]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar OpenAI do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: openAiModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-4o",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || ["continuar", "próximo", "avançar"],
        // Preservar 0 (0 = sem limite) ao re-hidratar valores no modo edit
        maxInteractions: typebotIntegration.maxInteractions ?? 5,
        completionTimeout: typebotIntegration.completionTimeout ?? 10,
        delay: typeof typebotIntegration.delay === "number" ? typebotIntegration.delay : 7,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
        departmentTransfers: Array.isArray(typebotIntegration.departmentTransfers)
          ? typebotIntegration.departmentTransfers.map((d) => ({
              queueId: Number(d.queueId) || 0,
              name: d.name || "",
              keywords: Array.isArray(d.keywords) ? d.keywords.join(", ") : (d.keywords || "")
            }))
          : [],
        userTransfers: Array.isArray(typebotIntegration.userTransfers)
          ? typebotIntegration.userTransfers.map((u) => ({
              userId: Number(u.userId) || 0,
              name: u.name || "",
              queueId: Number(u.queueId) || 0,
              keywords: Array.isArray(u.keywords) ? u.keywords.join(", ") : (u.keywords || "")
            }))
          : [],
        tagsAllowedForAI: Array.isArray(typebotIntegration.tagsAllowedForAI) ? typebotIntegration.tagsAllowedForAI : [],
        lanesAllowedForAI: Array.isArray(typebotIntegration.lanesAllowedForAI) ? typebotIntegration.lanesAllowedForAI : [],
        googleCalendarIntegrationId: Number(typebotIntegration.googleCalendarIntegrationId) > 0
          ? Number(typebotIntegration.googleCalendarIntegrationId)
          : 0,
        queueIdAfterCalendarCreate: Number(typebotIntegration.queueIdAfterCalendarCreate) || 0,
        queueIdAfterCalendarUpdate: Number(typebotIntegration.queueIdAfterCalendarUpdate) || 0,
        queueIdAfterCalendarCancel: Number(typebotIntegration.queueIdAfterCalendarCancel) || 0,
        sendLinksAsTextWhenVoice: Boolean(typebotIntegration.sendLinksAsTextWhenVoice),
        assetsAllowedForAI: Array.isArray(typebotIntegration.assetsAllowedForAI) ? typebotIntegration.assetsAllowedForAI : [],
        maxAttachmentsPerReply: Number(typebotIntegration.maxAttachmentsPerReply) > 0 ? Number(typebotIntegration.maxAttachmentsPerReply) : 2,
        ttsProvider:
          typebotIntegration.ttsProvider ||
          (azureVoices.includes(typebotIntegration.voice)
            ? "azure"
            : openAiTtsVoices.includes(typebotIntegration.voice)
              ? "openai"
              : "azure"),
        ttsModel: typebotIntegration.ttsModel || "tts-1",
        openaiTtsInstructions: typebotIntegration.openaiTtsInstructions || "",
        ttsSpeed: Number(typebotIntegration.ttsSpeed) || 1
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar OpenAI ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  useEffect(() => {
    if (open === "create" || open === "edit") {
      setPreviewText("Olá, aqui é um teste de voz do atendimento. Como posso te ajudar hoje?");
    }
  }, [open]);

  const handleClose = () => {
    releasePreview();
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const departmentTransfers = (values.departmentTransfers || [])
      .filter((d) => d && Number(d.queueId) > 0 && d.name)
      .map((d) => ({
        queueId: Number(d.queueId),
        name: String(d.name).trim(),
        keywords: typeof d.keywords === "string"
          ? d.keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : (Array.isArray(d.keywords) ? d.keywords : [])
      }));
    const userTransfers = (values.userTransfers || [])
      .filter((u) => u && Number(u.userId) > 0 && Number(u.queueId) > 0 && u.name)
      .map((u) => ({
        userId: Number(u.userId),
        name: String(u.name).trim(),
        queueId: Number(u.queueId),
        keywords: typeof u.keywords === "string"
          ? u.keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : (Array.isArray(u.keywords) ? u.keywords : [])
      }));
    const tagsAllowedForAI = (values.tagsAllowedForAI || [])
      .filter((t) => t && Number(t.tagId) > 0 && t.name)
      .map((t) => ({ tagId: Number(t.tagId), name: String(t.name).trim() }));
    const lanesAllowedForAI = (values.lanesAllowedForAI || [])
      .filter((l) => l && Number(l.tagId) > 0 && l.name)
      .map((l) => ({ tagId: Number(l.tagId), name: String(l.name).trim() }));
    const assetsAllowedForAI = (values.assetsAllowedForAI || [])
      .filter((a) => a && Number(a.assetId) > 0 && a.name)
      .map((a) => ({ assetId: Number(a.assetId), name: String(a.name).trim() }));
    const promptData = {
      ...values,
      queueId: Number(values.queueId) || 0,
      googleCalendarIntegrationId: Number(values.googleCalendarIntegrationId) > 0
        ? Number(values.googleCalendarIntegrationId)
        : 0,
      queueIdAfterCalendarCreate: Number(values.queueIdAfterCalendarCreate) || 0,
      queueIdAfterCalendarUpdate: Number(values.queueIdAfterCalendarUpdate) || 0,
      queueIdAfterCalendarCancel: Number(values.queueIdAfterCalendarCancel) || 0,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      departmentTransfers,
      userTransfers,
      tagsAllowedForAI,
      lanesAllowedForAI,
      assetsAllowedForAI,
      maxAttachmentsPerReply: Number(values.maxAttachmentsPerReply) > 0 ? Number(values.maxAttachmentsPerReply) : 2,
      sendLinksAsTextWhenVoice: Boolean(values.sendLinksAsTextWhenVoice),
      // Forçar provider como openai
      provider: "openai"
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim() && !integration.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const removeKeyword = (arrayHelpers, index) => {
    arrayHelpers.remove(index);
  };

  const getModelDisplayName = (model) => {
    const modelNames = {
      "gpt-3.5-turbo": "GPT 3.5 Turbo",
      "gpt-3.5-turbo-1106": "GPT 3.5 Turbo (1106)",
      "gpt-3.5-turbo-16k": "GPT 3.5 Turbo 16K",
      "gpt-4": "GPT 4",
      "gpt-4-turbo": "GPT 4 Turbo",
      "gpt-4o": "GPT 4o",
      "gpt-4o-mini": "GPT 4o Mini"
    };
    return modelNames[model] || model;
  };

  const getVoiceDisplayName = (voice) => {
    if (voice === "texto") return "Apenas Texto";
    if (azureVoices.includes(voice)) {
      return voice.replace("pt-BR-", "").replace("Neural", "");
    }
    return voice.charAt(0).toUpperCase() + voice.slice(1);
  };

  // [TTS OPCIONAL 778-L2] helpers do teste de voz (Azure default; OpenAI só por escolha explícita no nó)
  const inferTtsProvider = (voice, ttsProvider) => {
    if (ttsProvider === "azure" || ttsProvider === "openai") return ttsProvider;
    if (azureVoices.includes(voice)) return "azure";
    if (openAiTtsVoices.includes(voice)) return "openai";
    return "azure";
  };

  const getVoiceProfile = (voice, provider) => {
    const catalog = provider === "openai" ? openAiVoiceProfiles : azureVoiceProfiles;
    return catalog[voice] || { gender: "Neutra", tags: ["versátil"] };
  };

  const getVoiceCatalog = (provider) => {
    if (provider === "openai") {
      return openAiTtsVoices.map((voice) => ({
        value: voice,
        label: getVoiceDisplayName(voice),
        ...getVoiceProfile(voice, provider)
      }));
    }

    return azureVoices.map((voice) => ({
      value: voice,
      label: getVoiceDisplayName(voice),
      ...getVoiceProfile(voice, provider)
    }));
  };

  const playVoicePreview = async (values, voiceOverride) => {
    const provider = inferTtsProvider(voiceOverride || values.voice, values.ttsProvider);
    const selectedVoice = voiceOverride || values.voice;

    if (!previewText.trim()) {
      window.alert("Digite uma frase para testar a voz.");
      return;
    }

    setPreviewLoadingVoice(selectedVoice);

    try {
      await playPreview({
        text: previewText,
        ttsProvider: provider,
        voice: selectedVoice,
        voiceKey: values.voiceKey,
        voiceRegion: values.voiceRegion,
        apiKey: values.apiKey,
        ttsModel: values.ttsModel,
        openaiTtsInstructions: values.openaiTtsInstructions,
        ttsSpeed: values.ttsSpeed
      });
    } catch (error) {
      window.alert(error?.message || "Não foi possível gerar a demonstração de voz.");
    } finally {
      setPreviewLoadingVoice("");
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {labels.title}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={OpenAiSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configurações Básicas - OpenAI
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <Field
                      as={TextField}
                      label="Nome do Assistente"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={TextField}
                        label="API Key OpenAI"
                        name="apiKey"
                        type={showApiKey ? "text" : "password"}
                        error={touched.apiKey && Boolean(errors.apiKey)}
                        helperText={touched.apiKey && errors.apiKey}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.model && Boolean(errors.model)}
                    >
                      <InputLabel>Modelo OpenAI</InputLabel>
                      <Field
                        as={Select}
                        label="Modelo OpenAI"
                        name="model"
                      >
                        {openAiModels.map((model) => (
                          <MenuItem key={model} value={model}>
                            {getModelDisplayName(model)}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.model && errors.model && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.model}
                        </div>
                      )}
                    </FormControl>

                    <Field
                      as={TextField}
                      label="Prompt do Sistema"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={6}
                      multiline
                      placeholder="Descreva como a IA deve se comportar, que informações deve coletar, como deve responder..."
                    />

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText={touched.temperature && errors.temperature || "0 = conservador, 2 = criativo"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{
                          step: "0.1",
                          min: "0",
                          max: "2",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText={touched.maxTokens && errors.maxTokens || "Tamanho máximo da resposta"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText={touched.maxMessages && errors.maxMessages || "Histórico de contexto"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Delay da IA (segundos)"
                        name="delay"
                        error={touched.delay && Boolean(errors.delay)}
                        helperText={touched.delay && errors.delay || "Aguarda este tempo antes de responder (agrupa sequência de mensagens do contato). Padrão: 7"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, max: 60 }}
                      />
                    </div>

                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Event />
                      Google Calendar (opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Vincule uma integração para o agente listar, criar, atualizar e remover eventos (function calling).
                      Crie a integração (API ou painel), depois abra a URL retornada por{" "}
                      <code>GET /google-calendar-integrations/:id/oauth-url</code> para concluir o OAuth no Google.
                      {googleCalendarList[0]?.redirectUri && (
                        <> Redirect no Google Cloud Console: <code>{googleCalendarList[0].redirectUri}</code></>
                      )}
                    </Typography>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Integração Google Calendar</InputLabel>
                      <Field
                        as={Select}
                        label="Integração Google Calendar"
                        name="googleCalendarIntegrationId"
                      >
                        <MenuItem value={0}>Nenhuma</MenuItem>
                        {(googleCalendarList || []).map((g) => (
                          <MenuItem key={g.id} value={g.id}>
                            {g.name} {g.connected ? "(conectado)" : "(pendente OAuth)"}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                      Após ações no Google Calendar, pode transferir o ticket para uma fila (ex.: Comercial). O atendimento deixa de ser só IA (bot desligado neste ticket).
                    </Typography>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Fila após criar evento</InputLabel>
                      <Field
                        as={Select}
                        label="Fila após criar evento"
                        name="queueIdAfterCalendarCreate"
                      >
                        <MenuItem value={0}>Não transferir</MenuItem>
                        {(queues || []).map((q) => (
                          <MenuItem key={q.id} value={q.id}>
                            {q.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Fila após atualizar evento (remarcar)</InputLabel>
                      <Field
                        as={Select}
                        label="Fila após atualizar evento (remarcar)"
                        name="queueIdAfterCalendarUpdate"
                      >
                        <MenuItem value={0}>Não transferir</MenuItem>
                        {(queues || []).map((q) => (
                          <MenuItem key={q.id} value={q.id}>
                            {q.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Fila após cancelar evento</InputLabel>
                      <Field
                        as={Select}
                        label="Fila após cancelar evento"
                        name="queueIdAfterCalendarCancel"
                      >
                        <MenuItem value={0}>Não transferir</MenuItem>
                        {(queues || []).map((q) => (
                          <MenuItem key={q.id} value={q.id}>
                            {q.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURAÇÕES DE VOZ */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Chat />
                      Configurações de Voz (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                    >
                      <InputLabel>Tipo de Resposta</InputLabel>
                      <Select
                        label="Tipo de Resposta"
                        value={values.voice === "texto" ? "texto" : "audio"}
                        onChange={(e) => {
                          if (e.target.value === "texto") {
                            setFieldValue("voice", "texto");
                            return;
                          }
                          const provider = inferTtsProvider(values.voice, values.ttsProvider);
                          setFieldValue("ttsProvider", provider);
                          setFieldValue(
                            "voice",
                            provider === "openai" ? "nova" : "pt-BR-FabioNeural"
                          );
                        }}
                      >
                        <MenuItem value="texto">Apenas Texto</MenuItem>
                        <MenuItem value="audio">Responder em áudio (nota de voz)</MenuItem>
                      </Select>
                    </FormControl>

                    {values.voice !== "texto" && (
                      <>
                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Provedor de voz</InputLabel>
                          <Field
                            as={Select}
                            label="Provedor de voz"
                            name="ttsProvider"
                            onChange={(e) => {
                              const provider = e.target.value;
                              setFieldValue("ttsProvider", provider);
                              setFieldValue(
                                "voice",
                                provider === "openai" ? "nova" : "pt-BR-FabioNeural"
                              );
                            }}
                          >
                            <MenuItem value="azure">Microsoft Azure (PT-BR nativo)</MenuItem>
                            <MenuItem value="openai">OpenAI TTS (mesma API key do bloco)</MenuItem>
                          </Field>
                        </FormControl>

                        {(values.ttsProvider || "azure") === "azure" ? (
                          <>
                            <FormControl fullWidth margin="dense" variant="outlined" error={touched.voice && Boolean(errors.voice)}>
                              <InputLabel>Voz Azure</InputLabel>
                              <Field as={Select} label="Voz Azure" name="voice">
                                {azureVoices.map((voice) => (
                                  <MenuItem key={voice} value={voice}>
                                    {getVoiceDisplayName(voice)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                            <div className={classes.multFieldLine}>
                              <Field
                                as={TextField}
                                label="Voice Key (Azure)"
                                name="voiceKey"
                                error={touched.voiceKey && Boolean(errors.voiceKey)}
                                helperText={touched.voiceKey && errors.voiceKey}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                placeholder="Chave do Azure Speech Service"
                              />
                              <Field
                                as={TextField}
                                label="Voice Region (Azure)"
                                name="voiceRegion"
                                error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                helperText={touched.voiceRegion && errors.voiceRegion}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                placeholder="ex: brazilsouth"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <FormControl fullWidth margin="dense" variant="outlined">
                              <InputLabel>Modelo OpenAI TTS</InputLabel>
                              <Field as={Select} label="Modelo OpenAI TTS" name="ttsModel">
                                {openAiTtsModels.map((m) => (
                                  <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                            <FormControl fullWidth margin="dense" variant="outlined" error={touched.voice && Boolean(errors.voice)}>
                              <InputLabel>Voz OpenAI</InputLabel>
                              <Field as={Select} label="Voz OpenAI" name="voice">
                                {openAiTtsVoices.map((voice) => (
                                  <MenuItem key={voice} value={voice}>
                                    {getVoiceDisplayName(voice)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                            <Field
                              as={TextField}
                              label="Instruções de estilo (opcional)"
                              name="openaiTtsInstructions"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              multiline
                              rows={2}
                              placeholder="Ex.: Fale em português brasileiro, tom consultivo e profissional."
                              helperText="Recomendado para o modelo GPT-4o mini TTS"
                            />
                            <Field
                              as={TextField}
                              label="Velocidade da fala"
                              name="ttsSpeed"
                              type="number"
                              inputProps={{ min: 0.25, max: 4, step: 0.05 }}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              helperText="1 = normal (modelos tts-1 e tts-1-hd)"
                            />
                          </>
                        )}

                        <div className={classes.voicePreviewBox}>
                          <Typography variant="subtitle2" gutterBottom>
                            <VolumeUp fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
                            Teste e demonstração das vozes
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Use a frase abaixo para testar a voz selecionada ou clique em <b>Ouvir</b> ao lado de qualquer voz da lista.
                          </Typography>

                          <TextField
                            label="Frase de demonstração"
                            value={previewText}
                            onChange={(e) => setPreviewText(e.target.value)}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            multiline
                            rows={2}
                            helperText="Essa frase não é salva no fluxo; serve apenas para teste."
                          />

                          <div className={classes.voicePreviewActions}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<PlayArrow />}
                              onClick={() => playVoicePreview(values)}
                              disabled={Boolean(previewLoadingVoice)}
                            >
                              {previewLoadingVoice === values.voice ? "Gerando..." : "Ouvir voz selecionada"}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Stop />}
                              onClick={stopPreview}
                            >
                              Parar
                            </Button>
                          </div>

                          <div className={classes.voiceCatalog}>
                            <div className={classes.voiceCatalogHeader}>
                              <span>Voz</span>
                              <span>Perfil</span>
                              <span>Etiquetas</span>
                              <span>Demonstração</span>
                            </div>
                            {getVoiceCatalog(values.ttsProvider || "azure").map((voiceItem) => (
                              <div
                                key={voiceItem.value}
                                className={classes.voiceCatalogRow}
                                style={{
                                  backgroundColor:
                                    values.voice === voiceItem.value ? "rgba(25, 118, 210, 0.06)" : "transparent"
                                }}
                              >
                                <div>
                                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {voiceItem.label}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {voiceItem.value}
                                  </Typography>
                                </div>
                                <Typography variant="body2">{voiceItem.gender}</Typography>
                                <div className={classes.voiceTags}>
                                  {voiceItem.tags.map((tag) => (
                                    <Chip
                                      key={`${voiceItem.value}-${tag}`}
                                      label={tag}
                                      size="small"
                                      variant="outlined"
                                      className={classes.voiceTagChip}
                                    />
                                  ))}
                                </div>
                                <div>
                                  <Button
                                    size="small"
                                    variant={values.voice === voiceItem.value ? "contained" : "outlined"}
                                    color="primary"
                                    startIcon={<PlayArrow />}
                                    onClick={() => {
                                      setFieldValue("voice", voiceItem.value);
                                      playVoicePreview({ ...values, voice: voiceItem.value }, voiceItem.value);
                                    }}
                                    disabled={Boolean(previewLoadingVoice)}
                                  >
                                    {previewLoadingVoice === voiceItem.value ? "Gerando..." : "Ouvir"}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* COMPORTAMENTO DO FLUXO */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Timer />
                      Comportamento do Fluxo
                      <Tooltip title="Configure como o OpenAI deve se comportar no fluxo">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Permanente</strong> - Encerrar fluxo aqui
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                O usuário fica conversando com a IA até pedir transferência ou encerrar
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Temporário</strong> - Volta ao fluxo depois
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA executa uma tarefa específica e depois retorna ao fluxo normal
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* CONFIGURAÇÕES DO MODO TEMPORÁRIO */}
                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          ⏱️ Configurações do Modo Temporário
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Configure quando a IA deve parar e retornar ao fluxo
                        </Typography>

                        {/* Limite de Interações */}
                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          error={touched.maxInteractions && Boolean(errors.maxInteractions)}
                          helperText={touched.maxInteractions && errors.maxInteractions || "Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        {/* Timeout */}
                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          error={touched.completionTimeout && Boolean(errors.completionTimeout)}
                          helperText={touched.completionTimeout && errors.completionTimeout || "Tempo limite antes de voltar ao fluxo (0 = sem limite)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        {/* Palavras-chave de Continuação */}
                        <FormControl fullWidth margin="dense">
                          <Typography variant="subtitle2" gutterBottom>
                            Palavras-chave para Continuar Fluxo
                          </Typography>
                          <FieldArray name="continueKeywords">
                            {(arrayHelpers) => (
                              <div>
                                <Box display="flex" gap={1} alignItems="center" mb={1}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite uma palavra-chave"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        addKeyword(arrayHelpers, newKeyword);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                    disabled={!newKeyword.trim()}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {values.continueKeywords.map((keyword, index) => (
                                    <Chip
                                      key={index}
                                      label={keyword}
                                      className={classes.keywordChip}
                                      onDelete={() => removeKeyword(arrayHelpers, index)}
                                      deleteIcon={<Delete />}
                                      variant="outlined"
                                      size="small"
                                    />
                                  ))}
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Quando o usuário enviar uma dessas palavras, o fluxo continuará automaticamente
                                </Typography>
                                {touched.continueKeywords && errors.continueKeywords && (
                                  <Typography variant="caption" color="error">
                                    {errors.continueKeywords}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </FormControl>

                        {/* Objetivo */}
                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          error={touched.objective && Boolean(errors.objective)}
                          helperText={touched.objective && errors.objective || "Descreva o que a IA deve completar (ex: 'Coletar nome, email e telefone')"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ex: Coletar dados do cliente, Qualificar interesse, Diagnosticar problema..."
                        />

                        {/* Auto Completar */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                              name="autoCompleteOnObjective"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                Auto completar quando atingir objetivo
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA analisará automaticamente se completou o objetivo e voltará ao fluxo
                              </Typography>
                            </Box>
                          }
                        />

                        {/* Fila de Transferência */}
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          error={touched.queueId && Boolean(errors.queueId)}
                        >
                          <InputLabel id="flow-openai-temp-transfer-queue-label">
                            Fila (para transferência)
                          </InputLabel>
                          <Field
                            as={Select}
                            labelId="flow-openai-temp-transfer-queue-label"
                            id="flow-openai-temp-transfer-queue"
                            name="queueId"
                            label="Fila (para transferência)"
                          >
                            <MenuItem value={0}>Não transferir</MenuItem>
                            {queues.map((q) => (
                              <MenuItem key={q.id} value={q.id}>
                                {q.name}
                              </MenuItem>
                            ))}
                          </Field>
                          <FormHelperText>
                            {touched.queueId && errors.queueId
                              ? errors.queueId
                              : "Fila para onde transferir se o usuário pedir atendente (Não transferir = sem transferência)"}
                          </FormHelperText>
                        </FormControl>

                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* TRANSFERÊNCIA POR DEPARTAMENTO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <Business />
                      Transferência por departamento
                      <Tooltip title="Quando o contato pedir para falar com um departamento (ex: Comercial, Suporte), a IA reconhece e transfere com confirmação">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Adicione os departamentos (filas) e palavras-chave para reconhecimento. Ex.: contato diz &quot;quero falar com o comercial&quot; → transferência para a fila Comercial + mensagem de confirmação.
                    </Typography>
                    <FieldArray name="departmentTransfers">
                      {(arrayHelpers) => (
                        <>
                          {(values.departmentTransfers || []).map((item, index) => (
                            <Box key={index} display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mb={2} p={1} border={1} borderColor="divider" borderRadius={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Fila (departamento)</InputLabel>
                                <Field
                                  as={Select}
                                  name={`departmentTransfers.${index}.queueId`}
                                  label="Fila (departamento)"
                                >
                                  <MenuItem value={0}>Selecione a fila</MenuItem>
                                  {queues.map((q) => (
                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`departmentTransfers.${index}.name`}
                                label="Nome do departamento"
                                placeholder="Ex: Comercial"
                                variant="outlined"
                                size="small"
                                style={{ minWidth: 160 }}
                              />
                              <Field
                                as={TextField}
                                name={`departmentTransfers.${index}.keywords`}
                                label="Palavras-chave (opcional)"
                                placeholder="comercial, vendas, vendas por favor"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1, minWidth: 200 }}
                                helperText="Separadas por vírgula. Se vazio, usa o nome do departamento."
                              />
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ queueId: 0, name: "", keywords: "" })}
                          >
                            Adicionar departamento
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* TRANSFERÊNCIA PARA USUÁRIO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <Person />
                      Transferência para usuário (atendente)
                      <Tooltip title="Quando o contato pedir para falar com um atendente específico (ex: João), a IA reconhece se esse usuário está na lista, transfere para ele e para a fila dele">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Adicione os atendentes que podem ser solicitados pelo nome. Ex.: contato diz &quot;quero falar com o João&quot; → transferência para o usuário João e para a fila configurada (respeitando regras de fila).
                    </Typography>
                    <FieldArray name="userTransfers">
                      {(arrayHelpers) => (
                        <>
                          {(values.userTransfers || []).map((item, index) => (
                            <Box key={index} display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mb={2} p={1} border={1} borderColor="divider" borderRadius={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Usuário (atendente)</InputLabel>
                                <Field
                                  as={Select}
                                  name={`userTransfers.${index}.userId`}
                                  label="Usuário (atendente)"
                                  onChange={(e) => {
                                    const uid = Number(e.target.value);
                                    const user = usersList.find((u) => u.id === uid);
                                    if (user) {
                                      setFieldValue(`userTransfers.${index}.userId`, user.id);
                                      setFieldValue(`userTransfers.${index}.name`, user.name || "");
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione o usuário</MenuItem>
                                  {usersList.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`userTransfers.${index}.name`}
                                label="Nome (reconhecimento)"
                                placeholder="Ex: João"
                                variant="outlined"
                                size="small"
                                style={{ minWidth: 160 }}
                                helperText="Nome exibido ao contato; use o nome do usuário ou apelido"
                              />
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Fila do atendente</InputLabel>
                                <Field
                                  as={Select}
                                  name={`userTransfers.${index}.queueId`}
                                  label="Fila do atendente"
                                >
                                  <MenuItem value={0}>Selecione a fila</MenuItem>
                                  {queues.map((q) => (
                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`userTransfers.${index}.keywords`}
                                label="Palavras-chave (opcional)"
                                placeholder="joão, falar com joão"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1, minWidth: 200 }}
                                helperText="Separadas por vírgula. Se vazio, usa o nome."
                              />
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ userId: 0, name: "", queueId: 0, keywords: "" })}
                          >
                            Adicionar usuário
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* TAGS QUE A IA PODE ADICIONAR AO CONTATO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <LocalOffer />
                      Tags que a IA pode adicionar ao contato
                      <Tooltip title="A IA pode incluir na resposta 'Ação: Adicionar tag: [nome]' para marcar o contato com uma tag. Configure aqui quais tags estão permitidas.">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Selecione as tags que a IA pode adicionar ao contato quando fizer sentido (ex.: &quot;Interessado em vendas&quot;, &quot;Qualificado&quot;). A IA usará o nome exato da tag na resposta.
                    </Typography>
                    <FieldArray name="tagsAllowedForAI">
                      {(arrayHelpers) => (
                        <>
                          {(values.tagsAllowedForAI || []).map((item, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                <InputLabel>Tag</InputLabel>
                                <Field
                                  as={Select}
                                  name={`tagsAllowedForAI.${index}.tagId`}
                                  label="Tag"
                                  onChange={(e) => {
                                    const tag = tagsList.find((t) => t.id === Number(e.target.value));
                                    if (tag) {
                                      setFieldValue(`tagsAllowedForAI.${index}.tagId`, tag.id);
                                      setFieldValue(`tagsAllowedForAI.${index}.name`, tag.name);
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione uma tag</MenuItem>
                                  {tagsList.map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}
                          >
                            Adicionar tag permitida
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* LANES DO KANBAN QUE A IA PODE MOVER O CARD */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <ViewColumn />
                      Mover card no Kanban (por conversa)
                      <Tooltip title="A IA pode incluir na resposta 'Ação: Mover para lane: [nome]' para mover o card do ticket para outra coluna do Kanban. Configure aqui quais lanes estão permitidas.">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Selecione as lanes do Kanban para onde a IA pode mover o card quando a conversa indicar (ex.: &quot;Em andamento&quot;, &quot;Concluído&quot;). A IA usará o nome exato da lane na resposta.
                    </Typography>
                    <FieldArray name="lanesAllowedForAI">
                      {(arrayHelpers) => (
                        <>
                          {(values.lanesAllowedForAI || []).map((item, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                <InputLabel>Lane do Kanban</InputLabel>
                                <Field
                                  as={Select}
                                  name={`lanesAllowedForAI.${index}.tagId`}
                                  label="Lane do Kanban"
                                  onChange={(e) => {
                                    const lane = lanesList.find((l) => l.id === Number(e.target.value));
                                    if (lane) {
                                      setFieldValue(`lanesAllowedForAI.${index}.tagId`, lane.id);
                                      setFieldValue(`lanesAllowedForAI.${index}.name`, lane.name);
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione uma lane</MenuItem>
                                  {lanesList.map((lane) => (
                                    <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}
                          >
                            Adicionar lane permitida
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <AttachFile style={{ marginRight: 8, verticalAlign: "middle" }} />
                      Mídias e links (IA)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                      Somente assets cadastrados na biblioteca. A IA usa &quot;Ação: Enviar mídia: nome&quot;.
                    </Typography>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Assets permitidos para a IA</InputLabel>
                      <Select
                        multiple
                        value={(values.assetsAllowedForAI || []).map((a) => a.assetId)}
                        onChange={(e) => {
                          const ids = e.target.value;
                          const selected = assetsPicker
                            .filter((a) => ids.includes(a.id))
                            .map((a) => ({ assetId: a.id, name: a.name }));
                          setFieldValue("assetsAllowedForAI", selected);
                        }}
                        label="Assets permitidos para a IA"
                        renderValue={(selected) =>
                          selected
                            .map((id) => assetsPicker.find((a) => a.id === id)?.label || id)
                            .join(", ")
                        }
                      >
                        {assetsPicker.map((asset) => (
                          <MenuItem key={asset.id} value={asset.id}>
                            {asset.label} ({asset.name}) — {asset.type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Field
                      as={TextField}
                      label="Máximo de anexos por resposta"
                      name="maxAttachmentsPerReply"
                      type="number"
                      inputProps={{ min: 1, max: 5 }}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          type="checkbox"
                          name="sendLinksAsTextWhenVoice"
                          color="primary"
                        />
                      }
                      label="Enviar links em texto quando a resposta principal for áudio"
                    />
                  </AccordionDetails>
                </Accordion>

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderOpenAIModal;